import {
  Badge,
  Box,
  Card,
  HStack,
  Heading,
  IconButton,
  Input,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import { format } from "date-fns";
import * as React from "react";
import {
  LuChevronDown,
  LuCircleHelp,
  LuFileText,
  LuPause,
  LuPencil,
  LuPlay,
  LuSearch,
  LuTarget,
} from "react-icons/lu";
import { Link as RouterLink, useParams } from "react-router-dom";

import {
  CampaignCriteria,
  CampaignCriteriaKind,
  getCampaignCriteria,
} from "../api/campaign-criteria";
import { GoogleAdsAdGroup, getGoogleAdsAdGroup } from "../api/google-ads";
import {
  KeywordAgent,
  KeywordAgentStatus,
  KeywordAgentTerm,
  getKeywordAgent,
  getKeywordAgentTerms,
  pauseKeywordAgent,
  startKeywordAgent,
} from "../api/keyword-agent";
import { notFoundToNull } from "../api/not-found";
import { ActivityLog } from "../components/activity-log";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { Button } from "../components/button";
import DataTable, { DataTableColumn } from "../components/data-table";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../components/dialog";
import { EditCampaignCriteriaDialog } from "../components/edit-campaign-criteria-dialog";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import { InputGroup } from "../components/input-group";
import { MetricCard, MetricGroup } from "../components/metric-card";
import Page from "../components/page";
import PageBody from "../components/page-body";
import { ReviewSearchTermDialog } from "../components/review-search-term-dialog";
import {
  SearchTermNoteDialog,
  SearchTermNoteMode,
} from "../components/search-term-note-dialog";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "../components/segmented-control";
import { StartKeywordAgentDialog } from "../components/start-keyword-agent-dialog";
import { StatusPill } from "../components/status-pill";
import { toaster } from "../components/toaster";
import { useAppSelector } from "../hooks";
import { useGoogleAdsAccountName } from "../hooks/google-ads-account-name";
import { useGoogleAdsLoginCustomerId } from "../hooks/google-ads-login-customer-id";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { useWorkspaceURL } from "../hooks/workspace-url";
import { brand } from "../lib/brand";
import { formatStatus } from "../lib/format";
import {
  KEYWORD_AGENT_STATUS_LABELS,
  KEYWORD_AGENT_STATUS_TONES,
  formatPauseReason,
  getKeywordAgentDisplayStatus,
  isResumable,
} from "../lib/keyword-agent-status";
import { TermState, filterTerms } from "../lib/keyword-agent-terms";

function WhyRow({
  children,
  label,
}: {
  children: React.ReactNode;
  label: React.ReactNode;
}) {
  return (
    <HStack align="flex-start" gap="6">
      <Text
        color="fg.muted"
        fontSize="sm"
        flexShrink="0"
        width={"170px" as any}
      >
        {label}
      </Text>
      <Text fontSize="sm">{children}</Text>
    </HStack>
  );
}

// In the order the form asks them.
const criteriaRows = (criteria: CampaignCriteria) =>
  [
    { label: "What is this ad group offering", value: criteria.offer },
    { label: "Who is it for", value: criteria["target-customer"] },
    {
      label: "What do you want them to do",
      value: criteria["customer-action"],
    },
    {
      label: "Who looks like a fit but isn't",
      value: criteria["icp-but-bad-fit"],
    },
  ].filter((row) => row.value);

// Inlined into the spread below, this loses its contextual type — align
// widens to string and the row to any — so it is named out here.
const restoredColumn: DataTableColumn<KeywordAgentTerm> = {
  name: "Restored",
  align: "end",
  cell: ({ value: term }) => (
    <Text color="fg.muted">
      {term["restored-at"]
        ? format(new Date(term["restored-at"]), "MMM d, yyyy")
        : "—"}
    </Text>
  ),
};

export default function GoogleKeywordAgentPage() {
  const { customerId, campaignId, adGroupId } = useParams();
  const { loginCustomerId, withLoginCustomerId } =
    useGoogleAdsLoginCustomerId();
  const workspaceSlug = useAppSelector((state) => state.workspace.slug);
  const workspaceURL = useWorkspaceURL();
  const withToaster = useToaster();

  const [adGroup, setAdGroup] = React.useState<GoogleAdsAdGroup | null>(null);
  const [agent, setAgent] = React.useState<KeywordAgent | null>(null);
  const [criteria, setCriteria] = React.useState<CampaignCriteria | null>(null);
  const [terms, setTerms] = React.useState<KeywordAgentTerm[]>([]);
  const [reviewTerms, setReviewTerms] = React.useState<KeywordAgentTerm[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isStarting, withStarting] = useLoading();
  const [isPausing, withPausing] = useLoading();
  const [isPauseDialogOpen, setIsPauseDialogOpen] = React.useState(false);
  const [isCriteriaOpen, setIsCriteriaOpen] = React.useState(false);
  const [isEditCriteriaOpen, setIsEditCriteriaOpen] = React.useState(false);
  const [isStartDialogOpen, setIsStartDialogOpen] = React.useState(false);
  const [logTab, setLogTab] = React.useState<TermState>("excluded");
  const [termQuery, setTermQuery] = React.useState("");
  const [whyTerm, setWhyTerm] = React.useState<KeywordAgentTerm | null>(null);
  const [noteTerm, setNoteTerm] = React.useState<KeywordAgentTerm | null>(null);
  const [noteMode, setNoteMode] = React.useState<SearchTermNoteMode>("restore");
  const [isReviewOpen, setIsReviewOpen] = React.useState(false);
  // Not persisted: terms sitting unreviewed are what keep the agent from
  // going Active, so the prompt should come back on the next visit.
  const [isBannerDismissed, setIsBannerDismissed] = React.useState(false);

  const numericCampaignId = parseInt(campaignId || "0");
  const numericAdGroupId = parseInt(adGroupId || "0");
  const accountName = useGoogleAdsAccountName(workspaceSlug, customerId);
  const campaign = adGroup?.campaign;

  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "Google", to: workspaceURL("google-ads-accounts") },
    {
      label: accountName,
      to: withLoginCustomerId(
        workspaceURL(`google-ads-accounts/${customerId}/campaigns`),
      ),
    },
    {
      label: campaign?.name ?? "…",
      to: withLoginCustomerId(
        workspaceURL(
          `google-ads-accounts/${customerId}/campaigns/${campaignId}/ad-groups`,
        ),
      ),
    },
    { label: adGroup?.name ?? "…" },
  ]);

  // The agent, its criteria and its terms all 404 before an agent exists,
  // so notFoundToNull absorbs those before the toaster — which would
  // otherwise turn every un-agented ad group into a red toast — sees them.
  const loadAll = React.useCallback(async () => {
    if (!workspaceSlug || !customerId || !campaignId || !adGroupId) return;
    await withLoading(() =>
      withToaster(async () => {
        const adGroupData = await notFoundToNull(() =>
          getGoogleAdsAdGroup(
            workspaceSlug,
            customerId,
            loginCustomerId,
            numericCampaignId,
            numericAdGroupId,
          ),
        );
        setAdGroup(adGroupData);

        const agentData = await notFoundToNull(() =>
          getKeywordAgent(
            workspaceSlug,
            customerId,
            loginCustomerId,
            numericCampaignId,
            numericAdGroupId,
          ),
        );
        setAgent(agentData);

        setCriteria(
          agentData
            ? await notFoundToNull(() =>
                getCampaignCriteria(
                  workspaceSlug,
                  agentData["campaign-criteria-id"],
                ),
              )
            : null,
        );

        // Both log tabs, fetched together so switching doesn't re-query.
        const [excluded, restored] = agentData
          ? await Promise.all(
              (["excluded", "restored"] as const).map((view) =>
                notFoundToNull(() =>
                  getKeywordAgentTerms(
                    workspaceSlug,
                    customerId,
                    loginCustomerId,
                    numericCampaignId,
                    numericAdGroupId,
                    { view },
                  ),
                ),
              ),
            )
          : [null, null];
        setTerms([...(excluded?.items ?? []), ...(restored?.items ?? [])]);

        // Already ordered and capped by the backend.
        const review = agentData
          ? await notFoundToNull(() =>
              getKeywordAgentTerms(
                workspaceSlug,
                customerId,
                loginCustomerId,
                numericCampaignId,
                numericAdGroupId,
                { view: "review" },
              ),
            )
          : null;
        setReviewTerms(review?.items ?? []);
        setHasLoadedOnce(true);
      }),
    );
  }, [
    adGroupId,
    campaignId,
    customerId,
    loginCustomerId,
    numericAdGroupId,
    numericCampaignId,
    withLoading,
    withToaster,
    workspaceSlug,
  ]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const displayStatus = getKeywordAgentDisplayStatus({
    agent,
    "criteria-reviewed?": criteria?.["reviewed?"] ?? false,
  });
  const isActive = agent?.status === KeywordAgentStatus.ACTIVE;
  const stats = adGroup?.stats;
  // The tab labels carry their counts, so both are split out whether or
  // not they're the one on screen.
  const logTabs: SegmentedControlItem[] = React.useMemo(
    () =>
      (["excluded", "restored"] as const).map((state) => ({
        value: state,
        label: `${state === "excluded" ? "Excluded" : "Restored"} ${
          filterTerms(terms, { state }).length
        }`,
      })),
    [terms],
  );
  const visibleTerms = React.useMemo(
    () => filterTerms(terms, { state: logTab, query: termQuery }),
    [logTab, terms, termQuery],
  );

  // Resuming reuses the agent's row, so only a first start needs the
  // wizard.
  const resumeAgent = async () => {
    if (!agent) return;
    await withStarting(async () => {
      await withToaster(async () => {
        await startKeywordAgent(
          workspaceSlug,
          customerId!,
          loginCustomerId,
          numericCampaignId,
          numericAdGroupId,
          agent["campaign-criteria-id"],
        );
        toaster.create({ type: "success", title: "Agent resumed" });
        await loadAll();
      });
    });
  };

  const pauseAgent = async () => {
    await withPausing(async () => {
      await withToaster(async () => {
        const { "exclusions-remaining": remaining } = await pauseKeywordAgent(
          workspaceSlug,
          customerId!,
          loginCustomerId,
          numericCampaignId,
          numericAdGroupId,
        );
        setIsPauseDialogOpen(false);
        // Pausing promises the ad group is back the way the agent found
        // it, so say when it isn't.
        if (remaining > 0) {
          toaster.create({
            type: "warning",
            title: "Agent paused, with exclusions left behind",
            description: `Google wouldn't remove ${remaining} exclusion${remaining === 1 ? "" : "s"}. Resume and pause again to retry.`,
          });
        } else {
          toaster.create({
            type: "success",
            title: "Agent paused — nothing new will be excluded",
          });
        }
        await loadAll();
      });
    });
  };

  const pauseReason = formatPauseReason(agent?.["pause-reason"] ?? null);
  const canResume = isResumable(agent?.["pause-reason"] ?? null);
  // The ad group answers 404 for an id Google doesn't know, for one under
  // the wrong campaign, and for a disconnected integration. Without this
  // the heading is an ellipsis and Start is live against something that
  // isn't there.
  const isMissing = hasLoadedOnce && !adGroup;

  if (isMissing) {
    return (
      <Page>
        <Stack flex="1">
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={<IconCircle Icon={LuTarget} size="md" variant="flat" />}
                title="This ad group isn't available"
                description="It may have been removed in Google Ads, or Google Ads may need to be reconnected."
              >
                <Button asChild variant="surface">
                  <RouterLink
                    to={withLoginCustomerId(
                      workspaceURL(
                        `google-ads-accounts/${customerId}/campaigns/${campaignId}/ad-groups`,
                      ),
                    )}
                  >
                    Back to ad groups
                  </RouterLink>
                </Button>
              </EmptyState>
            </HStack>
          </PageBody>
        </Stack>
      </Page>
    );
  }

  return (
    <Page loading={isLoading && !hasLoadedOnce}>
      <Stack flex="1" gap="6">
        <Stack gap="0.5">
          <Heading fontSize={"22px" as any} fontWeight="medium">
            {adGroup?.name ?? "…"}
          </Heading>
          <Text color="fg.muted" fontSize="sm">
            {campaign
              ? [
                  campaign.name,
                  formatStatus(campaign["advertising-channel-type"]),
                  formatStatus(adGroup!.status),
                ].join(" · ")
              : ""}
          </Text>
        </Stack>

        <PageBody>
          <HStack justify="space-between" wrap="wrap" gap="3">
            <HStack gap="2">
              <Heading textStyle="h2">Keyword agent</Heading>
              <StatusPill tone={KEYWORD_AGENT_STATUS_TONES[displayStatus]}>
                {KEYWORD_AGENT_STATUS_LABELS[displayStatus]}
              </StatusPill>
            </HStack>
            {isActive ? (
              <Button
                variant="surface"
                loading={isPausing}
                onClick={() => setIsPauseDialogOpen(true)}
              >
                <LuPause size="16" />
                Pause Keyword Agent
              </Button>
            ) : (
              canResume && (
                <Button
                  variant="surface"
                  loading={isStarting}
                  onClick={
                    agent ? resumeAgent : () => setIsStartDialogOpen(true)
                  }
                >
                  <LuPlay size="16" />
                  {agent ? "Resume Keyword Agent" : "Start Keyword Agent"}
                </Button>
              )
            )}
          </HStack>

          {isActive ? (
            <MetricGroup>
              <MetricCard
                label="Negative Keywords Applied"
                value={(stats?.excluded ?? 0).toLocaleString()}
              />
              <MetricCard
                label="Search terms analyzed"
                value={(stats?.analyzed ?? 0).toLocaleString()}
              />
            </MetricGroup>
          ) : (
            <Box
              background="white"
              borderRadius="l3"
              boxShadow="card"
              maxWidth={"760px" as any}
              paddingX="6"
              paddingY="5"
            >
              <Text color="fg.muted" fontSize="sm" lineHeight={"1.6" as any}>
                {agent
                  ? `Keyword agent is paused. Your ad group is not being optimized.${pauseReason ? ` ${pauseReason}` : ""}`
                  : `${brand.productName} reads this ad group's search terms and excludes the ones that clearly aren't from your buyers. Every exclusion will be listed here, and you can undo any of them with one click.`}
              </Text>
            </Box>
          )}

          {isActive && !isBannerDismissed && (
            <Box
              background={
                reviewTerms.length > 0 ? ("warn.bg" as any) : "bg.subtle"
              }
              borderRadius="l3"
              boxShadow={reviewTerms.length > 0 ? "insetRing" : "card"}
              paddingX="6"
              paddingY="5"
            >
              <HStack align="flex-start" gap="4">
                <Stack gap="1" flex="1">
                  <Text fontSize="sm" fontWeight="medium">
                    {reviewTerms.length > 0
                      ? `${reviewTerms.length} pending review`
                      : `${brand.productName} is reading your search terms`}
                  </Text>
                  <Text
                    color="fg.muted"
                    fontSize="sm"
                    lineHeight={"1.6" as any}
                  >
                    {reviewTerms.length > 0
                      ? `Your answers teach ${brand.productName} how to evaluate terms it hasn't seen yet.`
                      : `Check back soon.`}
                  </Text>
                </Stack>
                {reviewTerms.length > 0 && (
                  <HStack gap="2" flexShrink="0">
                    <Button
                      variant="ghost"
                      onClick={() => setIsBannerDismissed(true)}
                    >
                      Dismiss
                    </Button>
                    <Button
                      variant="solid"
                      onClick={() => setIsReviewOpen(true)}
                    >
                      Review {reviewTerms.length}
                    </Button>
                  </HStack>
                )}
              </HStack>
            </Box>
          )}

          {criteria && (
            <Card.Root>
              {/* Plain conditional rather than Collapsible: the header
                  carries a button, which can't sit inside a trigger. */}
              <Card.Header
                cursor={"pointer" as any}
                onClick={() => setIsCriteriaOpen((open) => !open)}
                paddingBottom={isCriteriaOpen ? undefined : "6"}
              >
                <HStack gap="2" width="full">
                  <LuFileText size="18" />
                  <Heading textStyle="h2" fontSize="md">
                    Campaign criteria
                  </Heading>
                  <Badge>
                    {criteria.kind === CampaignCriteriaKind.PRESET
                      ? `From ${criteria.name}`
                      : "This ad group only"}
                  </Badge>
                  <Spacer />
                  <HStack gap="2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="surface"
                      onClick={() => setIsEditCriteriaOpen(true)}
                    >
                      <LuPencil size="14" />
                      Edit criteria
                    </Button>
                  </HStack>
                  <LuChevronDown
                    size="16"
                    style={{
                      transform: isCriteriaOpen ? "rotate(180deg)" : undefined,
                      transition: "transform .18s",
                    }}
                  />
                </HStack>
              </Card.Header>
              {isCriteriaOpen && (
                <Card.Body>
                  <Stack gap="2.5">
                    {criteriaRows(criteria).map((row) => (
                      <HStack key={row.label} align="flex-start" gap="6">
                        <Text
                          color="fg.muted"
                          fontSize="sm"
                          flexShrink="0"
                          width={"250px" as any}
                        >
                          {row.label}
                        </Text>
                        <Text fontSize="sm">{row.value}</Text>
                      </HStack>
                    ))}
                  </Stack>
                </Card.Body>
              )}
            </Card.Root>
          )}

          {agent && (
            <ActivityLog title="Excluded search terms">
              <Stack gap="4">
                <HStack
                  justify="space-between"
                  wrap="wrap"
                  gap="3"
                  paddingX="4"
                >
                  <SegmentedControl
                    items={logTabs}
                    value={logTab}
                    onValueChange={(value) => setLogTab(value as TermState)}
                  />
                  <InputGroup
                    startElement={<LuSearch size="16" />}
                    width={"260px" as any}
                    maxWidth="full"
                  >
                    <Input
                      placeholder="Filter search terms"
                      value={termQuery}
                      onChange={(e) => setTermQuery(e.target.value)}
                    />
                  </InputGroup>
                </HStack>

                {visibleTerms.length === 0 ? (
                  <Text color="fg.muted" fontSize="sm" padding="4">
                    {termQuery
                      ? `No excluded search terms match "${termQuery}".`
                      : logTab === "excluded"
                        ? "Nothing excluded yet."
                        : "Nothing has been restored."}
                  </Text>
                ) : (
                  <DataTable
                    bare
                    data={visibleTerms}
                    pagination
                    template={
                      logTab === "restored"
                        ? "minmax(200px,1.6fr) minmax(180px,1fr) 130px 150px"
                        : "minmax(200px,1.6fr) minmax(180px,1fr) 150px"
                    }
                    minTableWidth={logTab === "restored" ? "780px" : "650px"}
                    columns={[
                      {
                        name: "Search term",
                        cell: ({ value: term }) => (
                          <HStack gap="1.5" minWidth="0">
                            <Text
                              truncate
                              title={term.term}
                              textDecoration={
                                term["restored?"] ? "line-through" : undefined
                              }
                              color={term["restored?"] ? "fg.muted" : undefined}
                            >
                              {term.term}
                            </Text>
                          </HStack>
                        ),
                      },
                      {
                        // Usually not the term itself: one negative covers
                        // every term that shares its wording, so a value
                        // repeating down the column is the compression
                        // showing through.
                        name: "Blocked by",
                        cell: ({ value: term }) => {
                          const negative = term["blocked-by"];
                          return negative ? (
                            <HStack gap="1.5" minWidth="0">
                              <Text truncate title={negative.text}>
                                {negative.text}
                              </Text>
                              <Text color="fg.muted" flexShrink="0">
                                {formatStatus(negative["match-type"])}
                              </Text>
                            </HStack>
                          ) : (
                            <Text color="fg.muted">—</Text>
                          );
                        },
                      },
                      ...(logTab === "restored" ? [restoredColumn] : []),
                      {
                        name: "",
                        align: "end",
                        cell: ({ value: term }) => (
                          <HStack
                            gap="1"
                            justify="flex-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              aria-label={`Why ${brand.productName} excluded this`}
                              size="xs"
                              variant="ghost"
                              onClick={() => setWhyTerm(term)}
                            >
                              <LuCircleHelp size="14" />
                            </IconButton>
                            <Button
                              size="sm"
                              variant="surface"
                              onClick={() => {
                                setNoteMode(
                                  logTab === "excluded" ? "restore" : "exclude",
                                );
                                setNoteTerm(term);
                              }}
                            >
                              {logTab === "excluded" ? "Undo" : "Re-exclude"}
                            </Button>
                          </HStack>
                        ),
                      },
                    ]}
                  />
                )}
              </Stack>
            </ActivityLog>
          )}
        </PageBody>
      </Stack>

      <ReviewSearchTermDialog
        isOpen={isReviewOpen}
        onOpenChange={(details) => setIsReviewOpen(details.open)}
        onSuccess={loadAll}
        workspaceSlug={workspaceSlug}
        customerId={customerId!}
        loginCustomerId={loginCustomerId}
        campaignId={numericCampaignId}
        adGroupId={numericAdGroupId}
        terms={reviewTerms}
      />

      <SearchTermNoteDialog
        term={noteTerm}
        mode={noteMode}
        onOpenChange={(details) => {
          if (!details.open) setNoteTerm(null);
        }}
        onSuccess={loadAll}
        workspaceSlug={workspaceSlug}
        customerId={customerId!}
        loginCustomerId={loginCustomerId}
        campaignId={numericCampaignId}
        adGroupId={numericAdGroupId}
      />

      <DialogRoot
        open={!!whyTerm}
        onOpenChange={(details) => {
          if (!details.open) setWhyTerm(null);
        }}
      >
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>{whyTerm?.term}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap="4">
              {whyTerm?.reason && (
                <Stack gap="1">
                  <Text color="fg.muted" fontSize="sm">
                    Why {brand.productName} excluded it
                  </Text>
                  <Text fontSize="sm">{whyTerm.reason}</Text>
                </Stack>
              )}
              <Stack gap="2">
                {whyTerm?.["fit-score"] !== null &&
                  whyTerm?.["fit-score"] !== undefined && (
                    <WhyRow label="Fit score">
                      {whyTerm["fit-score"]}/10 based on your criteria
                    </WhyRow>
                  )}
                {whyTerm?.["excluded-at"] && (
                  <WhyRow label="Excluded">
                    {format(new Date(whyTerm["excluded-at"]), "MMM d, yyyy")}
                  </WhyRow>
                )}
                {whyTerm?.question && (
                  <WhyRow label={`${brand.productName} asked`}>
                    {whyTerm.question}
                  </WhyRow>
                )}
                {whyTerm?.["user-note"] && (
                  <WhyRow label={`You told ${brand.productName}`}>
                    &ldquo;{whyTerm["user-note"]}&rdquo;
                  </WhyRow>
                )}
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWhyTerm(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <StartKeywordAgentDialog
        isOpen={isStartDialogOpen}
        onOpenChange={(details) => setIsStartDialogOpen(details.open)}
        onSuccess={loadAll}
        workspaceSlug={workspaceSlug}
        customerId={customerId!}
        loginCustomerId={loginCustomerId}
        campaignId={numericCampaignId}
        adGroupId={numericAdGroupId}
        adGroupName={adGroup?.name ?? "This ad group"}
      />

      {criteria && (
        <EditCampaignCriteriaDialog
          isOpen={isEditCriteriaOpen}
          onOpenChange={(details) => setIsEditCriteriaOpen(details.open)}
          onSuccess={loadAll}
          workspaceSlug={workspaceSlug}
          customerId={customerId!}
          loginCustomerId={loginCustomerId}
          campaignId={numericCampaignId}
          adGroupId={numericAdGroupId}
          criteria={criteria}
        />
      )}

      <DialogRoot
        open={isPauseDialogOpen}
        onOpenChange={(details) => setIsPauseDialogOpen(details.open)}
      >
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Pause the keyword agent?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text color="fg.muted" fontSize="sm">
              Every exclusion {brand.productName} made comes off the ad group,
              and those terms can match your ads again. Nothing new will be
              excluded until you resume.
            </Text>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setIsPauseDialogOpen(false)}
              disabled={isPausing}
            >
              Cancel
            </Button>
            <Button variant="solid" loading={isPausing} onClick={pauseAgent}>
              Pause Keyword Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Page>
  );
}
